import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, setCsrf, UNAUTHORIZED } from './api/client'
import type { User } from './api/types'

interface AuthValue {
  user: User | null
  ready: boolean
  login: (username: string, password: string) => Promise<void>
  loginWithTelegram: (initData: string) => Promise<void>
  logout: () => Promise<void>
}

const Ctx = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [ready, setReady] = useState(false)

  const adopt = useCallback((u: User) => {
    setCsrf(u.csrf)
    setUser(u)
  }, [])

  useEffect(() => {
    api
      .get<User>('/api/me')
      .then(adopt)
      .catch(() => setUser(null))
      .finally(() => setReady(true))
  }, [adopt])

  useEffect(() => {
    const onUnauthorized = () => {
      setCsrf('')
      setUser(null)
    }
    window.addEventListener(UNAUTHORIZED, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED, onUnauthorized)
  }, [])

  const value = useMemo<AuthValue>(
    () => ({
      user,
      ready,
      async login(username, password) {
        adopt(await api.post<User>('/api/login', { username, password }))
      },
      async loginWithTelegram(initData: string) {
        adopt(await api.post<User>('/api/telegram/webapp', { initData }))
      },
      async logout() {
        // 服务端无论成败都会清掉 Cookie，所以本地状态必须照常落下；
        // 撤销失败由服务器记日志，不能把操作员卡在「看起来还登录着」的壳子里。
        try {
          await api.post('/api/logout')
        } catch {
          /* 本地退出照常完成 */
        } finally {
          setCsrf('')
          setUser(null)
        }
      },
    }),
    [user, ready, adopt],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth must be used inside AuthProvider')
  return v
}
